import { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, Row, Table } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { useNavigate, useParams, Link } from "react-router-dom";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { Formik } from "formik";
import AppInputField from "components/shared/FormComponents/AppInputField";
import LoadingButton from "components/shared/LoadingButton";
import { ASSESSMENT_TEMPLATE_LIST } from "config/paths";
import { successMessageToast } from "helpers/common";
import { CommonSelect, RequiredFieldAstrick } from "components/Common/Common";
import CKEditorField from "components/CkEditor/CkEditor";
import { useCreateAssessmentTemplateMutation, useGetAssessmentTemplateQuery, useGetAssessmentTemplateTableTypeListQuery, useUpdateAssessmentTemplateMutation } from "store/services/assessmentTemplate";
import TherapistListing from "../Components/TherapistListing";
import validationSchema from "./validation.schema";
import CharacterCounter from "components/shared/CharacterCounter";
import FormError from "components/shared/FormComponents/FormError";
import AssessmentTable from "../Components/AssessmentTable";

const TITLE_LENGTH = 60;

const AddAssessmentTemplate = () => {
    const { id } = useParams();
    const editMode = !!id;
    const navigate = useNavigate();
    const divisonId = ""
    const [selectedTherapists, setSelectedTherapists] = useState([]);

    const { data: templateData, isLoading: templateDataLoading, isError: templateDataError } = useGetAssessmentTemplateQuery({ id }, { skip: !id });
    const [createTemplate, { isLoading }] = useCreateAssessmentTemplateMutation();
    const [updateTemplate, { isLoading: updateTemplateLoading }] = useUpdateAssessmentTemplateMutation();

    const { data: tableTypeList, isLoading: tableTypesLoading } = useGetAssessmentTemplateTableTypeListQuery({ limit: 9999 });

    const tableTypeOptions =
    tableTypeList?.records?.length > 0
        ? tableTypeList?.records?.map(tableType => ({
            value: tableType.id,
            label: tableType.title,
            raw: tableType
        }))
        : [];

    const initialValues = useMemo(() => {
        return {
            title: templateData?.title || "",
            description: templateData?.description || "",
            tableType: templateData?.associatedTableType
                ? (
                    {
                        value: templateData?.associatedTableType?.id,
                        label: templateData?.associatedTableType?.title,
                        raw: templateData?.associatedTableType,
                    }
                )
                : null,
            assignTherapists: templateData?.templateAssignedTherapists?.length
                ? templateData?.templateAssignedTherapists?.map(item => item.therapistId)
                : [],
            chartData: templateData?.chartData || [],
        }
    }, [templateData]);

    useEffect(() => {
        setSelectedTherapists(initialValues?.assignTherapists);
    }, [initialValues?.assignTherapists])

    const handleClickCancel = () => {
        navigate(ASSESSMENT_TEMPLATE_LIST);
    }

    const handleSubmit = async (values) => {
        try {
            const body = {
                ...values,
                tableType: values?.tableType?.value,
                assignTherapists: selectedTherapists
            }
            let result;
            if (editMode) {
                result = await updateTemplate({
                  id,
                  body
                }).unwrap();
            } else {
                result = await createTemplate({ body }).unwrap();
            }

            successMessageToast(result.data?.message || "Assessment template created successfully");
            navigate(ASSESSMENT_TEMPLATE_LIST);
        } catch (err) {
            console.error("Error creating assessment template", err);
        }
    }

    return (
        <>
            <section className={`${styles.addForm} position-relative py-4 addForm`}>
                <Container fluid={true}>
                    <Row>
                        <Col lg="12" className="my-2">
                            <div className="pageHead pb-3 d-flex align-items-center">
                                <Link
                                    className="themeBlue fw-sbold back-to-btn"
                                    to={ASSESSMENT_TEMPLATE_LIST}
                                >
                                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                                    </svg> Back
                                </Link>
                                <h6 className="m-0 fw-bold themeBlue">
                                    Assessment Template
                                </h6>
                            </div>
                        </Col>
                        <Col lg="12" className="my-2">
                            {templateDataLoading ? (
                                <LoadingIndicator />
                            ) : templateDataError ? (
                                <ErrorComponent />
                            ) : (
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema(TITLE_LENGTH)}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({
                                        values,
                                        errors,
                                        handleSubmit,
                                        setFieldValue,
                                    }) => (
                                        <Form onSubmit={handleSubmit}>
                                            <div
                                                className="py-3"
                                                style={{ borderTop: "1px solid #41a2a84d" }}
                                            >
                                                <Row>
                                                    <Col md="4" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Name of Assessment
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <p className="text-muted"><em>Aim for 60 characters. The full name can be included in the description below.</em></p>
                                                    </Col>
                                                    <Col md="8" sm="12" className="my-2">
                                                        <div className="position-relative">
                                                            <AppInputField
                                                                name="title"
                                                                placeholder="Enter Assessment Name"
                                                                className={`${styles.formControl} form-control`}
                                                                errorStyles={{ position: "absolute" }}
                                                            />
                                                            <div className="d-flex justify-content-end position-absolute end-0">
                                                                <CharacterCounter
                                                                    value={values?.title}
                                                                    maxLength={TITLE_LENGTH}
                                                                />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md="4" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Assessment Description
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <p className="text-muted"><em>If the full name of the assessment does not fit in the field above and an abbreviation was used, start this description with the full name of the assessment in bold.</em></p>
                                                    </Col>
                                                    <Col md="8" sm="12" className="my-2">
                                                        <div className={`ck-editor-wrapper small`} >
                                                            <CKEditorField
                                                                handleChange={(event, editor) => setFieldValue("description", editor?.getData())}
                                                                value={initialValues?.description}
                                                            />
                                                            {errors?.description ? (
                                                                <div className="error">{errors.description}</div>
                                                            ) : null}
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row className="pt-3">
                                                    <Col md="4" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Assessment Table
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <p className="text-muted"><em>Choose a Table Type</em></p>
                                                    </Col>
                                                    <Col md="8" sm="12" className="my-2">
                                                        <div className={`ck-editor-wrapper small`} >
                                                            <CommonSelect
                                                                name="tableType"
                                                                placeholder="Choose a Table Type"
                                                                classNamePrefix={"khan"}
                                                                option={tableTypeOptions}
                                                                isLoading={tableTypesLoading}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md="12" className="mt-2 mb-3">
                                                        <AssessmentTable
                                                            type={values?.tableType?.raw}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    {/* <Col md="12" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Assign to Therapists
                                                        </label>
                                                    </Col> */}
                                                    <Col md="12" sm="12" className="my-2">
                                                        <TherapistListing
                                                            selectedTherapists={selectedTherapists}
                                                            setSelectedTherapists={setSelectedTherapists}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                            <Row>
                                                <Col className="my-2" lg="12">
                                                    <FormError />
                                                </Col>
                                                <Col className="my-2" lg="12">
                                                    <div
                                                        className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                                                        style={{ borderTop: "1px solid #41a2a84d" }}
                                                    >
                                                        <LoadingButton
                                                            type="submit"
                                                            className="d-flex align-items-center justify-content-center commonBtn"
                                                            isLoading={isLoading || updateTemplateLoading}
                                                        >
                                                            Save
                                                        </LoadingButton>
                                                        <Button
                                                            onClick={handleClickCancel}
                                                            className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default AddAssessmentTemplate;